import { Alert, Spin, Table } from 'antd'
import { ColumnsType } from 'antd/es/table/interface'
import { Breakpoint } from 'antd/lib/_util/responsiveObserver'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import {
  EpoRestrictionItem,
  RestrictionCheckboxData,
} from '../../../common/epoTypes'
import { Rizeni } from '../../../common/proceedingTypes'
import { numberSorter, stringSorter } from '../../utils/generalTableHelpers'
import { tablePagination } from '../../utils/layoutConst'
import { parseProceedingString } from '../../utils/proceedingDetailHelpers'
import { restrictionColumn } from '../../utils/propertyMonitoringHelpers'
import { useColumnSearchFilter } from '../../utils/useColumnSearchFilter'
import { EpoRestrictionTableData } from '../../utils/useEpoRestrictionTableData'
import { CuzkEpoRestrictionCheckPanel } from '../CuzkEpoRestrictionCheckPanel/CuzkEpoRestrictionCheckPanel'
import { LvDetailDrawerLink } from '../LvDetailDrawerLink/LvDetailDrawerLink'
import { MapTooltip } from '../MapTooltip/MapTooltip'
import { ProceedingDetailModalLink } from '../ProceedingDetailModalLink/ProceedingDetailModalLink'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'
import { TableSummaryExport } from '../TableSummaryExport/TableSummaryExport'

export interface CuzkEpoRestrictionTableProps {
  epoId: string
  isHugeSubject: boolean
  checkboxData: RestrictionCheckboxData
  dataSource: EpoRestrictionTableData
  refetch: () => void
}

const tableId = 'epoRestriction-table'
const scrollX = { x: true as const }
const propertyRenderer = (property: number) => (
  <span className={property ? 'pr-2' : 'pr-2 text-gray-400'}>{property}</span>
)

export const CuzkEpoRestrictionTable: FunctionComponent<CuzkEpoRestrictionTableProps> =
  props => {
    const positiveLiens = useMemo(
      () => props.dataSource.data?.positiveLiens,
      [props.dataSource.data]
    )
    const { getSearchFilter: searchFilter } =
      useColumnSearchFilter<EpoRestrictionItem>()

    const columns = useMemo<ColumnsType<EpoRestrictionItem>>(
      () => [
        {
          title: 'ID',
          dataIndex: 'id',
          responsive: ['' as Breakpoint],
        },
        {
          title: 'Kat. úřad',
          dataIndex: 'katastralniUradNazev',
          sorter: stringSorter('katastralniUradNazev'),
          ...searchFilter('katastralniUradNazev', 'Vyhledat kat. úřad'),
          render: katastralniUradNazev => (
            <MapTooltip query={katastralniUradNazev}>
              {katastralniUradNazev}
            </MapTooltip>
          ),
        },
        {
          title: 'Kat. pracoviště',
          dataIndex: 'katastralniPracovisteNazev',
          sorter: stringSorter('katastralniPracovisteNazev'),
          ...searchFilter(
            'katastralniPracovisteNazev',
            'Vyhledat kat. pracoviště'
          ),
          render: katastralniPracovisteNazev => (
            <MapTooltip query={katastralniPracovisteNazev}>
              {katastralniPracovisteNazev}
            </MapTooltip>
          ),
        },
        {
          title: 'Obec',
          dataIndex: 'obec',
          sorter: stringSorter('obec'),
          ...searchFilter('obec', 'Vyhledat obec'),
        },
        {
          title: 'Kat. území',
          dataIndex: 'katastralniUzemiNazev',
          sorter: stringSorter('katastralniUzemiNazev'),
          ...searchFilter(
            'katastralniUzemiNazev',
            'Vyhledat katastrální území'
          ),
          render: (katastralniUzemiNazev, record) => (
            <MapTooltip query={record.obec + ' ' + katastralniUzemiNazev}>
              {katastralniUzemiNazev}
            </MapTooltip>
          ),
        },

        {
          title: 'LV',
          dataIndex: 'lv',
          className: '!text-right',
          sorter: numberSorter('lv'),
          ...searchFilter('lv', 'Vyhledat LV'),
          render: (lv, record) => (
            <LvDetailDrawerLink
              lvCode={record.katastralniUzemiKod}
              lvNumber={lv}
            />
          ),
        },
        {
          title: 'Bud.',
          dataIndex: 'budovyPocet',
          className: 'w-10 !text-right !pr-0',
          showSorterTooltip: { title: 'Seřadit dle počtu budov na LV' },
          sorter: numberSorter('budovyPocet'),
          render: propertyRenderer,
        },
        {
          title: 'Par.',
          dataIndex: 'parcelyPocet',
          className: 'w-10 !text-right !pr-0',
          showSorterTooltip: { title: 'Seřadit dle počtu parcel na LV' },
          sorter: numberSorter('parcelyPocet'),
          render: propertyRenderer,
        },
        {
          title: 'Jed.',
          dataIndex: 'jednotkyPocet',
          className: 'w-10 !text-right !pr-0',
          showSorterTooltip: { title: 'Seřadit dle počtu jednotek na LV' },
          sorter: numberSorter('jednotkyPocet'),
          render: propertyRenderer,
        },
        {
          title: 'Řízení',
          dataIndex: 'rizeni',
          className: '!text-right',
          ...searchFilter('rizeni', 'Vyhledat řízení'),
          render: rizeni =>
            rizeni?.map((item: string, index: number) => (
              <ProceedingDetailModalLink
                key={index}
                className="block"
                proceeding={parseProceedingString(item) as Rizeni}
              >
                {item.replace(/-\d+$/, '')}
              </ProceedingDetailModalLink>
            )),
        },
        restrictionColumn.exekuce,
        restrictionColumn.insolvence,
        restrictionColumn.zastavyNedobrovolne,
        restrictionColumn.predkupniPravo,
      ],
      [searchFilter]
    )

    const locale = useMemo(
      () => ({
        emptyText: props.dataSource.loading ? (
          <TableSkeleton rows={10} />
        ) : !positiveLiens?.length ? (
          <div className="my-8">
            <div className="mb-2 text-gray-400">
              Pro vyhledaný subjekt nebyly nalezeny v evidenci práv ČÚZK žádné
              záznamy.
            </div>
          </div>
        ) : undefined,
      }),
      [positiveLiens?.length, props.dataSource.loading]
    )

    const summary = useCallback(
      () => (
        <TableSummaryExport
          total={positiveLiens?.length || 0}
          colSpan={9}
          colSpanAction={4}
          url="cuzk/epoLiensXlsx"
          fileName="prava-cizich-subjektu"
          dataSource={positiveLiens || []}
          icon
        />
      ),
      [positiveLiens]
    )

    if (props.dataSource.error)
      return (
        <Alert
          message="Chyba dat"
          description="Nepodařilo se načíst data z ČÚZK."
          type="error"
          className="my-8"
          showIcon
        />
      )

    if (props.dataSource.loading && !positiveLiens?.length)
      return (
        <div className="my-8 flex flex-col justify-center align-middle bg-white">
          <Spin spinning />
          <div className="mb-2 mt-2 text-center text-gray-400">
            Načítají se data z ČÚZK...
          </div>
        </div>
      )

    if (!positiveLiens?.length && !!props.dataSource?.data?.lvTotalCount)
      return (
        <CuzkEpoRestrictionCheckPanel
          isHugeSubject={props.isHugeSubject}
          epoId={props.epoId}
          refetch={props.refetch}
          hasBeenChecked={!!props.dataSource?.data?.hasBeenChecked}
          checkboxData={props.checkboxData}
        />
      )

    if (!positiveLiens) return null

    return (
      <Table
        id={tableId}
        rowKey="id"
        size="small"
        scroll={scrollX}
        dataSource={positiveLiens}
        columns={columns}
        loading={props.dataSource.loading}
        locale={locale}
        summary={summary}
        pagination={tablePagination}
      />
    )
  }
